import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PouchValueKeyboardSession } from '../context/PouchValueKeyboardContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  autoAdjustPvkPositionAvoidingAnchor,
  clampPvkStoredPosition,
  findPvkKeyboardAnchorElement,
  initialPvkPosition,
  measurePvkFooterClearancePx,
  resolvePvkPixelX,
  writePvkStoredPosition,
  type PvkStoredPosition,
} from '../utils/pouchValueKeyboardPosition';
import { POUCH_EXPRESSION_KEYBOARD_SYMBOLS } from '../utils/pouchValueInput';
import './styles/PouchValueKeyboard.scss';

const COMPACT_LAYOUT_MQ = '(max-width: 520px)';

function useCompactKeyboardLayout(): boolean {
  const [compact, setCompact] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(COMPACT_LAYOUT_MQ).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_LAYOUT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return compact;
}

interface PouchValueKeyboardPanelProps {
  session: PouchValueKeyboardSession;
  visible?: boolean;
  onTransitionEnd?: (e: React.TransitionEvent<HTMLDivElement>) => void;
}

export function PouchValueKeyboardPanel({
  session,
  visible = true,
  onTransitionEnd,
}: PouchValueKeyboardPanelProps) {
  const { appCopy } = useLocale();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PvkStoredPosition>(initialPvkPosition);
  const [positionReady, setPositionReady] = useState(false);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originBottom: number } | null>(null);

  const minBottomPxRef = useRef(measurePvkFooterClearancePx());
  minBottomPxRef.current = measurePvkFooterClearancePx();

  const computeAutoPosition = useCallback((): PvkStoredPosition => {
    const panel = panelRef.current;
    const preferred = initialPvkPosition();
    if (!panel) {
      return preferred;
    }
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;
    if (panelWidth <= 0 || panelHeight <= 0) {
      return preferred;
    }

    const anchor = findPvkKeyboardAnchorElement(session.pouchId);
    if (!anchor) {
      return preferred;
    }

    return autoAdjustPvkPositionAvoidingAnchor(
      anchor.getBoundingClientRect(),
      panelWidth,
      panelHeight,
      preferred,
      minBottomPxRef.current,
    );
  }, [session.pouchId]);

  const applyAutoPositionForSession = useCallback(() => {
    setPosition(computeAutoPosition());
  }, [computeAutoPosition]);

  useLayoutEffect(() => {
    setPositionReady(false);
    let raf = 0;
    let cancelled = false;

    const tryPlace = () => {
      if (cancelled) {
        return;
      }
      const panel = panelRef.current;
      if (!panel || panel.offsetWidth <= 0 || panel.offsetHeight <= 0) {
        raf = window.requestAnimationFrame(tryPlace);
        return;
      }
      setPosition(computeAutoPosition());
      setPositionReady(true);
    };

    tryPlace();
    return () => {
      cancelled = true;
      if (raf) {
        window.cancelAnimationFrame(raf);
      }
    };
  }, [session.id, session.pouchId, session.variant, computeAutoPosition]);

  useEffect(() => {
    if (!positionReady) {
      return;
    }
    const onViewportChange = () => {
      applyAutoPositionForSession();
    };
    window.visualViewport?.addEventListener('resize', onViewportChange);
    window.visualViewport?.addEventListener('scroll', onViewportChange);
    window.addEventListener('resize', onViewportChange);
    return () => {
      window.visualViewport?.removeEventListener('resize', onViewportChange);
      window.visualViewport?.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [positionReady, applyAutoPositionForSession]);

  const resolvePixelPosition = useCallback((pos: PvkStoredPosition) => {
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const minBottom = minBottomPxRef.current;
    const xPx = resolvePvkPixelX(pos, vw);
    const bottomPx = Math.max(pos.bottom, minBottom);
    return { xPx, bottomPx, vw, vh, minBottom };
  }, []);

  const onGrabPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const { xPx, bottomPx } = resolvePixelPosition(position);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: xPx,
        originBottom: bottomPx,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [position, resolvePixelPosition],
  );

  const onGrabPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      e.preventDefault();
      const { vw, vh, minBottom } = resolvePixelPosition(position);
      const panel = panelRef.current;
      const panelH = panel?.offsetHeight ?? 96;
      const panelW = panel?.offsetWidth ?? 360;
      const margin = 8;

      let xPx = drag.originX + (e.clientX - drag.startX);
      let bottomPx = drag.originBottom - (e.clientY - drag.startY);

      const halfW = panelW / 2;
      xPx = Math.max(halfW + margin, Math.min(vw - halfW - margin, xPx));
      bottomPx = Math.max(minBottom, Math.min(vh - panelH - margin, bottomPx));

      const next: PvkStoredPosition = {
        x: xPx / vw,
        bottom: Math.max(minBottom, bottomPx),
      };
      setPosition(next);
    },
    [position, resolvePixelPosition],
  );

  const onGrabPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setPosition(prev => {
      writePvkStoredPosition(prev);
      return prev;
    });
  }, []);

  useEffect(() => {
    const syncFooterClearance = () => {
      minBottomPxRef.current = measurePvkFooterClearancePx();
      setPosition(prev => clampPvkStoredPosition(prev));
    };
    syncFooterClearance();
    window.addEventListener('resize', syncFooterClearance);
    return () => window.removeEventListener('resize', syncFooterClearance);
  }, []);

  const onKey = useCallback(
    (text: string) => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      session.insertText(text);
    },
    [session],
  );

  const preventInputBlur = useCallback((e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
  }, []);

  const onAction = useCallback(
    (action: 'backspace' | 'confirm' | 'cancel' | 'goToTitle') => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (action === 'backspace') {
        session.backspace();
      } else if (action === 'confirm') {
        session.confirm();
      } else if (action === 'goToTitle') {
        session.goToTitle?.();
      } else {
        session.cancel?.();
      }
    },
    [session],
  );

  const undoIcon = (
    <svg
      className="pvk-glyph-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 10h10a5 5 0 0 1 5 5v2" />
      <path d="M3 10l6 6" />
      <path d="M3 10l6-6" />
    </svg>
  );

  const confirmIcon = (
    <svg
      className="pvk-glyph-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );

  const compactLayout = useCompactKeyboardLayout();
  const { xPx, bottomPx } = resolvePixelPosition(position);
  const decimalKey = session.isTime ? ':' : '.';
  const isExpression = session.variant === 'expression';
  const hasTitleNav = !!session.goToTitle;

  type SymbolKey = {
    label: string;
    insert: string;
    action?: 'backspace' | 'confirm' | 'cancel' | 'goToTitle';
  };

  const withTitleNavKey = (keys: SymbolKey[]): SymbolKey[] => {
    if (!hasTitleNav) {
      return keys;
    }
    const confirmIndex = keys.findIndex(k => k.action === 'confirm');
    if (confirmIndex < 0) {
      return keys;
    }
    const next = [...keys];
    next.splice(confirmIndex, 0, { label: '↑', insert: '', action: 'goToTitle' });
    return next;
  };

  const compactNumberRow1 = ['1', '2', '3', '4', '5', decimalKey];
  const compactNumberRow2 = ['6', '7', '8', '9', '0', '00'];
  const desktopNumberRow = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', decimalKey];

  const expressionSymbolKeys: SymbolKey[] = POUCH_EXPRESSION_KEYBOARD_SYMBOLS.map(s => ({
    label: s.label,
    insert: s.insert,
  }));

  const symbolKeys: SymbolKey[] = withTitleNavKey(
    isExpression
      ? [
          { label: '', insert: '', action: 'cancel' },
          ...expressionSymbolKeys,
          { label: '⌫', insert: '', action: 'backspace' },
          { label: '', insert: '', action: 'confirm' },
        ]
      : [
          { label: '', insert: '', action: 'cancel' },
          { label: '+', insert: '+' },
          { label: '−', insert: '-' },
          { label: '%', insert: '%' },
          { label: '⌫', insert: '', action: 'backspace' },
          { label: '', insert: '', action: 'confirm' },
        ],
  );

  const renderKey = (
    key: SymbolKey,
    className: string,
    keyId?: string,
    extraClass = '',
  ) => {
    const isAction = !!key.action;
    const handler = isAction ? onAction(key.action!) : onKey(key.insert);
    const actionClass =
      key.action === 'confirm'
        ? ' pvk-key--confirm'
        : key.action === 'cancel'
          ? ' pvk-key--cancel'
          : key.action === 'goToTitle'
            ? ' pvk-key--title-nav'
            : '';
    return (
      <button
        key={keyId ?? key.label}
        type="button"
        className={`pvk-key ${className}${isAction ? ' pvk-key--action' : ''}${actionClass}${extraClass}`}
        onMouseDown={preventInputBlur}
        onPointerDown={handler}
        aria-label={
          key.action === 'backspace'
            ? appCopy.keyboard.backspace
            : key.action === 'confirm'
              ? appCopy.keyboard.confirm
              : key.action === 'cancel'
                ? appCopy.keyboard.cancel
                : key.action === 'goToTitle'
                  ? appCopy.keyboard.editTitle
                  : undefined
        }
      >
        {key.action === 'cancel'
          ? undoIcon
          : key.action === 'confirm'
            ? confirmIcon
            : key.label}
      </button>
    );
  };

  const renderSymbolKey = (k: SymbolKey, corner = '') =>
    renderKey(
      k,
      'pvk-key--symbol',
      k.action ? `sym-${k.action}` : `sym-${k.label}`,
      corner,
    );

  const renderSymbolRow = (keys: SymbolKey[], rowClass = '') => (
    <div className={`pvk-row pvk-row--symbols${rowClass}`}>
      {keys.map(k => {
        let corner = '';
        if (k.action === 'cancel') corner = ' pvk-key--corner-bl';
        if (k.action === 'confirm') corner = ' pvk-key--corner-br';
        return renderSymbolKey(k, corner);
      })}
    </div>
  );

  const renderCompactExpressionSymbols = () => (
    <>
      <div className="pvk-row pvk-row--symbols pvk-row--symbols-compact-top">
        {expressionSymbolKeys.slice(0, 7).map(k => renderSymbolKey(k))}
        {renderSymbolKey(
          { label: '⌫', insert: '', action: 'backspace' },
          ' pvk-key--corner-tr',
        )}
      </div>
      <div className="pvk-row pvk-row--symbols pvk-row--symbols-compact-bottom">
        {renderSymbolKey({ label: '', insert: '', action: 'cancel' }, ' pvk-key--corner-bl')}
        {expressionSymbolKeys.slice(7).map(k => renderSymbolKey(k))}
        {hasTitleNav &&
          renderSymbolKey({ label: '↑', insert: '', action: 'goToTitle' })}
        {renderSymbolKey({ label: '', insert: '', action: 'confirm' }, ' pvk-key--corner-br')}
      </div>
    </>
  );

  const renderCompactMultiplierSymbols = () => (
    <>
      <div className="pvk-row pvk-row--symbols pvk-row--symbols-multiplier-top">
        {renderSymbolKey({ label: '+', insert: '+' })}
        {renderSymbolKey({ label: '−', insert: '-' })}
        {renderSymbolKey({ label: '⌫', insert: '', action: 'backspace' }, ' pvk-key--corner-tr')}
      </div>
      <div className="pvk-row pvk-row--symbols pvk-row--symbols-multiplier-bottom">
        {renderSymbolKey({ label: '', insert: '', action: 'cancel' }, ' pvk-key--corner-bl')}
        {renderSymbolKey({ label: '%', insert: '%' })}
        {renderSymbolKey({ label: '', insert: '', action: 'confirm' }, ' pvk-key--corner-br')}
      </div>
    </>
  );

  return (
    <div
      ref={panelRef}
      className={`pouch-value-keyboard-panel${visible && positionReady ? ' is-visible' : ''}${
        compactLayout ? ' pouch-value-keyboard-panel--compact' : ''
      }${hasTitleNav ? ' pouch-value-keyboard-panel--title-nav' : ''}${
        session.variant === 'multiplier' ? ' pouch-value-keyboard-panel--multiplier' : ''
      }`}
      data-pouch-value-keyboard="true"
      data-variant={session.variant}
      style={{ left: `${xPx}px`, bottom: `${bottomPx}px` }}
      onTransitionEnd={onTransitionEnd}
      onPointerDown={e => e.preventDefault()}
    >
      <div
        className="pvk-grab-bar"
        role="presentation"
        aria-label={appCopy.keyboard.moveKeyboard}
        onMouseDown={preventInputBlur}
        onPointerDown={onGrabPointerDown}
        onPointerMove={onGrabPointerMove}
        onPointerUp={onGrabPointerUp}
        onPointerCancel={onGrabPointerUp}
      />
      <div className="pvk-keys">
        {compactLayout ? (
          <>
            <div className="pvk-row pvk-row--numbers">
              {compactNumberRow1.map((d, i, arr) =>
                renderKey(
                  { label: d, insert: d },
                  'pvk-key--digit',
                  `num-${d}`,
                  i === 0 ? ' pvk-key--corner-tl' : i === arr.length - 1 ? ' pvk-key--corner-tr' : '',
                ),
              )}
            </div>
            <div className="pvk-row pvk-row--numbers">
              {compactNumberRow2.map(d =>
                renderKey({ label: d, insert: d }, 'pvk-key--digit', `num-${d}`),
              )}
            </div>
          </>
        ) : (
          <div className="pvk-row pvk-row--numbers">
            {desktopNumberRow.map((d, i, arr) =>
              renderKey(
                { label: d, insert: d },
                'pvk-key--digit',
                `num-${d}`,
                i === 0 ? ' pvk-key--corner-tl' : i === arr.length - 1 ? ' pvk-key--corner-tr' : '',
              ),
            )}
          </div>
        )}
        {compactLayout && isExpression
          ? renderCompactExpressionSymbols()
          : compactLayout
            ? renderCompactMultiplierSymbols()
            : renderSymbolRow(symbolKeys)}
      </div>
    </div>
  );
}
